import React, { useEffect, useMemo, useState } from "react";
import ResellerShareTree from "../ResellerShareTree";
import StoryOpensCard from "../StoryOpensCard";
import LeakCheckCard from "../LeakCheckCard";
import { isCatalogueResellerSharingEnabled } from "../../../helpers/catalogueResellerSharingEnabled";
import { isCatalogueViewerGateEnabled } from "../../../helpers/catalogueViewerGateEnabled";
import { fetchCatalogueViewers, type CatalogueViewerRow } from "../catalogueEngagementApi";
import {
  formatViewerDuration,
  topPiecesLine,
  totalViewerSeconds,
  viewerDisplayName,
  viewerLastSeen,
  viewerPhoneLabel,
  viewersInRange,
} from "../catalogueViewers";
import { Row, Col, Card, CardHeader, CardBody, Button } from "reactstrap";
import { Catalogue } from "../../../types/catalogue.types";
import {
  fetchCartSubmissionsForCatalogue,
  fetchWishlistsForCatalogue,
} from "../catalogueShopperSignals";
import {
  activityRangeStart,
  buildCustomerActivityFeed,
  catalogueWhatsAppHref,
  expiredOrDisabledLinkCount,
  filterCustomerActivity,
  uniqueBuyersInRange,
  type CustomerActivityFilter,
  type CustomerActivityItem,
} from "../customerActivityFeed";

export type ActivityFollowUpTarget = {
  inquiryId?: string;
  submissionId?: string;
};

interface ActivityTabProps {
  catalogue: Catalogue;
  onOpenFollowUp?: (target: ActivityFollowUpTarget) => void;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const KIND_LABEL: Record<string, string> = {
  access: "Open",
  inquiry: "Inquiry",
  extension: "Extension",
  selection: "Selection",
  share: "Share",
  security: "Security",
};

const KIND_CLASS: Record<string, string> = {
  access: "text-success",
  inquiry: "text-info",
  extension: "text-warning",
  selection: "text-primary",
  share: "text-secondary",
  security: "text-danger",
};

const ActivityTab: React.FC<ActivityTabProps> = ({ catalogue, onOpenFollowUp }) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activityFilter, setActivityFilter] = useState<CustomerActivityFilter>("buyer");
  const [timeRange, setTimeRange] = useState("30d");
  const [feed, setFeed] = useState<CustomerActivityItem[]>([]);
  const viewersOn = isCatalogueViewerGateEnabled();
  const [viewers, setViewers] = useState<CatalogueViewerRow[]>([]);
  const [metrics, setMetrics] = useState({
    shares: 0,
    uniqueBuyers: 0,
    expiredLinks: 0,
    securityEvents: 0,
  });

  useEffect(() => {
    const loadActivity = async () => {
      if (!catalogue?.id) return;
      const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
      const headers = authHeaders();
      const start = activityRangeStart(timeRange);

      try {
        setLoading(true);
        setLoadError(null);
        setFeed([]);

        // Who viewed, with time spent. Its failure never hides the rest of the timeline.
        const viewersPromise = viewersOn
          ? fetchCatalogueViewers(catalogue.id).catch(() => ({ success: false as const, data: undefined }))
          : Promise.resolve({ success: false as const, data: undefined });

        const [viewsRes, linksRes, eventsRes, inquiriesRes, wishlists, carts] =
          await Promise.all([
            fetch(`${base}/catalogue-analytics/${catalogue.id}/views?limit=200&inviteesOnly=true`, {
              headers,
            }),
            fetch(`${base}/catalogue-security/${catalogue.id}/links`, { headers }),
            fetch(`${base}/catalogue-security/${catalogue.id}/security-events?limit=100`, { headers }),
            fetch(`${base}/catalogue-inquiry/catalogue/${encodeURIComponent(catalogue.id)}`, {
              headers,
            }),
            fetchWishlistsForCatalogue(catalogue.id),
            fetchCartSubmissionsForCatalogue(catalogue.id),
          ]);

        const [views, linksJson, eventsJson, inquiriesJson] = await Promise.all([
          viewsRes.json().catch(() => ({})),
          linksRes.json().catch(() => ({})),
          eventsRes.json().catch(() => ({})),
          inquiriesRes.json().catch(() => ({})),
        ]);

        if (!viewsRes.ok && !linksRes.ok) {
          setLoadError("Could not load activity for this catalogue.");
          setFeed([]);
          setMetrics({ shares: 0, uniqueBuyers: 0, expiredLinks: 0, securityEvents: 0 });
          return;
        }

        const links = linksJson?.success && Array.isArray(linksJson.data) ? linksJson.data : [];
        const events = eventsJson?.success && Array.isArray(eventsJson.data) ? eventsJson.data : [];
        const inquiries =
          inquiriesJson?.success && Array.isArray(inquiriesJson.data) ? inquiriesJson.data : [];
        const viewsList = Array.isArray(views?.data) ? views.data : [];
        const viewersJson = await viewersPromise;
        setViewers(viewersJson?.success && Array.isArray(viewersJson.data) ? viewersJson.data : []);

        const combined = buildCustomerActivityFeed({
          views: viewsList,
          links,
          inquiries,
          events,
          wishlists,
          carts,
          start,
        });

        const eventsInRange = events.filter((e: any) => {
          const t = e?.createdAt;
          return t != null && new Date(t).getTime() >= start.getTime();
        });

        setFeed(combined);
        setMetrics({
          shares: links.filter((l: any) => l?.createdAt && new Date(l.createdAt).getTime() >= start.getTime())
            .length,
          uniqueBuyers: uniqueBuyersInRange(viewsList, start),
          expiredLinks: expiredOrDisabledLinkCount(links, start),
          securityEvents: eventsInRange.length,
        });
      } catch {
        setLoadError("Could not load activity for this catalogue.");
        setFeed([]);
        setMetrics({ shares: 0, uniqueBuyers: 0, expiredLinks: 0, securityEvents: 0 });
      } finally {
        setLoading(false);
      }
    };

    loadActivity();
  }, [catalogue?.id, timeRange, viewersOn]);

  const filteredActivities = useMemo(
    () => filterCustomerActivity(feed, activityFilter),
    [feed, activityFilter]
  );
  const viewersShown = useMemo(
    () => viewersInRange(viewers, activityRangeStart(timeRange)),
    [viewers, timeRange]
  );

  const MetricCard: React.FC<{
    title: string;
    value: number;
    footnote: string;
  }> = ({ title, value, footnote }) => (
    <Card className="h-100 catalogue-panel">
      <CardBody className="text-center">
        <div className="text-muted small mb-1">{title}</div>
        <div className="h3 fw-bold mb-2">{value}</div>
        <small className="text-muted d-block" style={{ fontSize: "11px" }}>
          {footnote}
        </small>
      </CardBody>
    </Card>
  );

  const rangeLabel = timeRange === "7d" ? "Last 7 days" : timeRange === "90d" ? "Last 90 days" : "Last 30 days";

  const openFollowUp = (row: CustomerActivityItem) => {
    if (!onOpenFollowUp) return;
    if (row.inquiryId || row.submissionId) {
      onOpenFollowUp({ inquiryId: row.inquiryId, submissionId: row.submissionId });
    }
  };

  return (
    <div>
      <div className="catalogue-tab-toolbar">
        <h3 className="catalogue-tab-heading mb-0">Activity</h3>
        <div className="d-flex gap-2">
          <select
            className="form-select"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ width: "auto" }}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>
      <p className="text-muted small mb-3">
        Buyer timeline for this catalogue. Studio preview opens are excluded. Live shelf drafts stay on
        Customer Inquiries until the visitor submits.
      </p>
      {loading && <p className="text-muted">Loading activity...</p>}
      {loadError && <p className="text-danger small">{loadError}</p>}
      {isCatalogueResellerSharingEnabled() && catalogue?.id && !catalogue.systemManaged ? (
        <div className="mb-4">
          <ResellerShareTree catalogueId={catalogue.id} />
        </div>
      ) : null}
      {catalogue?.id && !catalogue.systemManaged ? (
        <div className="mb-4">
          <StoryOpensCard catalogueId={catalogue.id} />
        </div>
      ) : null}
      {catalogue?.id && !catalogue.systemManaged ? (
        <div className="mb-4">
          <LeakCheckCard catalogueId={catalogue.id} />
        </div>
      ) : null}

      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Share links"
            value={metrics.shares}
            footnote={`Issued in ${rangeLabel.toLowerCase()}`}
          />
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Unique buyers"
            value={metrics.uniqueBuyers}
            footnote={`Invitees with a last open in ${rangeLabel.toLowerCase()}`}
          />
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Expired / disabled"
            value={metrics.expiredLinks}
            footnote={`Links in ${rangeLabel.toLowerCase()}`}
          />
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Security events"
            value={metrics.securityEvents}
            footnote={`Audit rows in ${rangeLabel.toLowerCase()}`}
          />
        </Col>
      </Row>

      {viewersOn ? (
        <Card className="mb-4" data-testid="catalogue-viewers">
          <CardHeader style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="catalogue-panel-title mb-0">Who viewed</h6>
              <span className="text-muted small">
                {viewersShown.length} {viewersShown.length === 1 ? "person" : "people"} ·{" "}
                {formatViewerDuration(totalViewerSeconds(viewersShown))} in total
              </span>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {viewersShown.length === 0 ? (
              <p className="text-muted small m-3 mb-3">
                No one in this range yet. Share an Open link that asks for a name, and each viewer shows here
                with the pieces they looked at and how long they stayed.
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr className="small text-muted">
                      <th>Viewer</th>
                      <th className="text-end">Opens</th>
                      <th className="text-end">Time</th>
                      <th className="text-end">Pieces</th>
                      <th>Most viewed</th>
                      <th>Last seen</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {viewersShown.map((row) => {
                      const seen = viewerLastSeen(row);
                      const phoneLabel = viewerPhoneLabel(row);
                      const wa = catalogueWhatsAppHref(
                        row.phone,
                        `Hi ${viewerDisplayName(row)}, following up on the catalogue.`
                      );
                      return (
                        <tr key={row.viewerUserId}>
                          <td>
                            <div className="fw-medium small">{viewerDisplayName(row)}</div>
                            {row.phone ? (
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                ···{String(row.phone).slice(-4)}
                                {phoneLabel ? (
                                  <span className={`ms-1 ${phoneLabel === "verified" ? "text-success" : "text-warning"}`}>
                                    {phoneLabel === "verified" ? "✓ verified" : "unverified"}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </td>
                          <td className="text-end small">{row.opens}</td>
                          <td className="text-end small">{formatViewerDuration(row.activeSeconds)}</td>
                          <td className="text-end small">{row.piecesViewed}</td>
                          <td className="small">{topPiecesLine(row) || <span className="text-muted">—</span>}</td>
                          <td className="small text-muted">{seen ? seen.toLocaleString("en-IN") : "—"}</td>
                          <td className="text-end">
                            {wa ? (
                              <a href={wa} target="_blank" rel="noopener noreferrer" className="small">
                                WhatsApp
                              </a>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}

      <Row>
        <Col lg={8} className="mb-4">
          <Card className="h-100">
            <CardHeader style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h6 className="catalogue-panel-title mb-0">Buyer timeline</h6>
                <select
                  className="form-select form-select-sm"
                  value={activityFilter}
                  onChange={(e) => setActivityFilter(e.target.value as CustomerActivityFilter)}
                  style={{ width: "auto" }}
                >
                  <option value="buyer">Buyers</option>
                  <option value="all">All</option>
                  <option value="access">Opens</option>
                  <option value="inquiry">Inquiries</option>
                  <option value="extension">Extensions</option>
                  <option value="selection">Selections</option>
                  <option value="share">Shares</option>
                  <option value="security">Security</option>
                </select>
              </div>
            </CardHeader>
            <CardBody>
              <div style={{ maxHeight: "440px", overflowY: "auto" }}>
                {filteredActivities.map((activity) => {
                  const clickable = Boolean(activity.inquiryId || activity.submissionId);
                  const wa = catalogueWhatsAppHref(
                    activity.clientPhone,
                    `Hi ${activity.clientName || ""}, following up on the catalogue.`
                  );
                  return (
                    <div
                      key={activity.id}
                      className="d-flex align-items-start mb-3 pb-3 border-bottom"
                    >
                      <div className="me-3" style={{ minWidth: "5.5rem" }}>
                        <span className={`small fw-semibold ${KIND_CLASS[activity.kind] || "text-muted"}`}>
                          {KIND_LABEL[activity.kind] || "Activity"}
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-medium small mb-1">{activity.description}</div>
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          {new Date(activity.atMs).toLocaleString("en-IN")}
                          {activity.clientPhone && (
                            <span className="ms-2">
                              • ···{activity.clientPhone.slice(-4)}
                            </span>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {clickable && onOpenFollowUp && (
                            <Button
                              color="link"
                              size="sm"
                              className="p-0"
                              onClick={() => openFollowUp(activity)}
                            >
                              Open in Inquiries
                            </Button>
                          )}
                          {wa && (
                            <a
                              href={wa}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="small"
                            >
                              WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {!loading && filteredActivities.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">
                    No buyer activity in this range yet. Opens appear after an invitee verifies OTP.
                    Submitted shortlists, carts, inquiries, and extension requests also show here.
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={4} className="mb-4">
          <Card className="h-100">
            <CardHeader style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
              <h6 className="catalogue-panel-title">In this range</h6>
            </CardHeader>
            <CardBody>
              <div className="small">
                <div className="d-flex justify-content-between mb-2">
                  <span>Rows in feed</span>
                  <strong>{feed.length}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Opens</span>
                  <strong>{feed.filter((x) => x.kind === "access").length}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Inquiries + extensions</span>
                  <strong>
                    {feed.filter((x) => x.kind === "inquiry" || x.kind === "extension").length}
                  </strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Submitted selections</span>
                  <strong>{feed.filter((x) => x.kind === "selection").length}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Security events</span>
                  <strong>{metrics.securityEvents}</strong>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ActivityTab;
